import {brandUrl, collectionsUrl, collectionUrl, productUrl, sellerUrl} from './url';

export const SCHEMA_CONTEXT = {
    '@context': 'https://schema.org/'
};

export function organizationSchemaOrg(properties) {
    return  {
        ...properties,
        '@type': 'Organization', // Naver channels don't seem to be compatible the more precise Corporation type
        name: 'FruitsFamily',
        legalName: '(주)후루츠패밀리컴퍼니',
        logo: 'https://fruitsfamily.com/logo512.png',
        url: 'https://fruitsfamily.com',
        sameAs: [
            'https://www.instagram.com/fruitsfamily.co/',
            'https://www.facebook.com/FruitsFamily-108768747380238/',
            'https://itunes.apple.com/app/fruitsfamily/id1466193804',
            'https://play.google.com/store/apps/details?id=com.fruitsfamily'
        ]
    };
}

export function reviewSchemaOrg(reviews, properties) {
    return  {
        ...properties,
        '@type': 'Review',
        reviewRating: {
            '@type': 'Rating',
            ratingValue: reviews[0].rating
        },
        author: {
            '@type': 'Person',
            name: reviews[0].user.username
        },
        reviewBody: reviews[0].text,
        datePublished: reviews[0].createdAt
    };
}

export function aggregateRatingSchemaOrg(product, properties) {
    if (product.seller.review_count > 0) {
        return {
            ...properties,
            '@type': 'AggregateRating',
            ratingValue: product.seller.rating,
            reviewCount: product.seller.review_count,
        };
    }
}

export function sellerSchemaOrg(seller, properties) {
    return {
        ...properties,
        '@type': 'Store',
        name: seller.user.nickname,
        alternateName: `@${seller.user.username}`,
        description: seller.user.bio,
        image: seller.user.resizedBigImage,
        mainEntityOfPage: sellerUrl(seller),
        aggregateRating: seller.review_count > 0 ?
            {
                '@type': 'AggregateRating',
                ratingValue: seller.rating,
                reviewCount: seller.review_count,
            } : undefined
    };
}


export function productSchemaOrg(product, properties) {
    return {
        ...properties,
        '@type': 'Product',
        name: product.title,
        image: product.resizedBigImages,
        description: product.description,
        sku: product.id,
        brand: product.brand ?
            {
                '@type': 'Brand',
                name: product.brand
            } :
            undefined,
        category: product.category,
        mainEntityOfPage: productUrl(product),
        review: product.seller.reviews && product.seller.reviews.length ?
            reviewSchemaOrg(product.seller.reviews) :
            undefined,
        aggregateRating: aggregateRatingSchemaOrg(product),
        offers: {
            '@type': 'Offer',
            url: productUrl(product),
            priceCurrency: 'KRW', // TODO store the currency in the product
            price: product.price,
            itemCondition: product.condition === 'NEW' ? 'https://schema.org/NewCondition' : 'https://schema.org/UsedCondition',
            availability: product.status === 'sold' ? 'https://schema.org/SoldOut' : 'https://schema.org/InStock',
            seller: sellerSchemaOrg(product.seller)
        }
    };
}

export function brandSchemaOrg(brand, properties, hostname = []) {
    const brandSlug = brand.name.toLowerCase().replace(/\s+/g, '-');
    const brandPageUrl = hostname ? `${hostname}${brandUrl(brand.name)}` : brandUrl(brand.name);
    const brandMetaDescription = brand.description ? 
        `${brand.description.trim()} | ${brand.name}의 빈티지 세컨핸드 상품을 후루츠에서 한번에.` : 
        `${brand.name}의 빈티지 세컨핸드 상품을 후루츠에서 한번에.`;

    const brandName = brand.name_kr ? `${brand.name_kr}(${brand.name})` : brand.name;
    
    return {
        ...properties,
        "@graph": [
            {
                "@type": "WebPage",
                "name": `${brandName} | 당신의 최애 브랜드를 후루츠패밀리에서 새롭게 발견하세요`,
                "url": brandPageUrl,
                "description": brandMetaDescription,
                "breadcrumb": {
                    "@type": "BreadcrumbList",
                    "itemListElement": [
                        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://fruitsfamily.com" },
                        { "@type": "ListItem", "position": 2, "name": brandName, "item": brandPageUrl }
                    ]
                },
                "mainEntity": { "@id": `#${brandSlug}-brand` }
            },
            {
                "@type": "Brand",
                "@id": `#${brandSlug}-brand`,
                "name": brandName,
                "url": brandPageUrl,
                "logo": brand.resizedSmallLogo,
                "image": brand.resizedBigLogo || brand.resizedSmallLogo,
                "description": brand.description || undefined,
                "interactionStatistic": {
                    "@type": "InteractionCounter",
                    "interactionType": { "@type": "FollowAction" },
                    "userInteractionCount": brand.follow_count,
                },
                "hasPart": {
                    "@type": "OfferCatalog",
                    "name": `${brand.name} 상품 목록`,
                    "numberOfItems": brand.product_count,
                }
            }
        ]
    };
}

/**
 * Breadcrumb 스키마 생성
 */
export const breadcrumbSchemaOrg = (breadcrumbs) => {
    return {
        "@type": "BreadcrumbList",
        "itemListElement": breadcrumbs.map((breadcrumb, index) => ({
            "@type": "ListItem",
            "position": index + 1,
            "name": breadcrumb.name,
            "item": breadcrumb.url
        }))
    };
};

/**
 * 컬렉션 목록 페이지 스키마 생성
 */
export const collectionsPageSchemaOrg = (collections = [], baseUrl) => {
    if (!Array.isArray(collections)) {
        return null;
    }

    const collectionsList = collections || [];
    const pageTitle = '기획전 모아보기 | 후루츠패밀리 - 빈티지, 세컨핸드 패션 중고거래';
    const pageDescription = '트렌디한 아이템부터 숨은 취향템까지, 후루츠패밀리가 매주 큐레이팅한 기획전을 한눈에 만나보세요. 빈티지, 프리미엄 세컨핸드 패션부터 감도 높은 악세사리까지—당신의 취향을 자극할 컬렉션이 기다리고 있어요.';

    const itemList = collectionsList.slice(0, 10).map((collection, index) => {
        if (!collection) return null;
        
        const url = baseUrl + collectionUrl(collection);
        const title = collection.title || '기획전';
        const description = collection.description || '';
        const image = collection.resizedBigImage || collection.resizedSmallImage || '';
        
        return {
            "@type": "ListItem",
            "position": index + 1,
            "item": {
                "@type": "Collection",
                "name": title,
                ...(description && { "description": description }),
                "url": url,
                ...(image && { "image": image }),
                "creator": {
                    "@type": "Organization",
                    "name": "후루츠패밀리",
                    "url": baseUrl,
                }
            }
        };
    }).filter(Boolean);

    return {
        "@context": SCHEMA_CONTEXT,
        "@type": "CollectionPage",
        "name": pageTitle,
        "description": pageDescription,
        "url": `${baseUrl}${collectionsUrl()}`,
        "mainEntity": {
            "@type": "ItemList",
            "name": pageTitle,
            "description": pageDescription,
            "numberOfItems": itemList.length,
            "itemListElement": itemList
        },
        "publisher": {
            "@type": "Organization",
            "name": "후루츠패밀리",
            "logo": `${baseUrl}/logo512.png`,
            "url": baseUrl,
        },
        "inLanguage": "ko-KR",
        "isPartOf": {
            "@type": "WebSite",
            "name": "후루츠패밀리",
            "url": baseUrl,
        }
    };
};


/**
 * 컬렉션 페이지 스키마 생성
 */
export const collectionSchemaOrg = (collection, baseUrl) => {
    if (!collection) return null;

    const url = baseUrl + collectionUrl(collection) || '';
    const title = collection.title || '기획전';
    const description = collection.description || '';
    const image = collection.resizedBigImage || collection.resizedSmallImage || '';
    
    return {
        "@context": SCHEMA_CONTEXT,
        "@type": "Collection",
        "name": title,
        "alternateName": collection.subtitle || undefined,
        "description": description,
        "url": url,
        ...(image && {
            "image": {
                "@type": "ImageObject",
                "url": image,
            }
        }),
        "creator": {
            "@type": "Organization",
            "name": "후루츠패밀리",
            "logo": `${baseUrl}/logo512.png`,
            "url": baseUrl,
        },
        ...(collection.featured_from && { "datePublished": collection.featured_from }),
        ...(collection.updatedAt && { "dateModified": collection.updatedAt }),
        "publisher": {
            "@type": "Organization",
            "name": "후루츠패밀리",
            "logo": `${baseUrl}/logo512.png`,
            "url": baseUrl,
        },
        "mainEntityOfPage": {
            "@type": "WebPage",
            "url": url
        },
        "inLanguage": "ko-KR",
        "isPartOf": {
            "@type": "WebSite",
            "name": "후루츠패밀리",
            "url": baseUrl,
        }
    };
};

/**
 * 검색 결과용 ItemList 스키마 생성
 * @param {Object[]} products - 상품 배열 (id, title/name, image 등 포함)
 * @param {string} [keyword] - 검색 키워드(옵션)
 * @returns {Object} ItemList 스키마
 */
export function itemListSchemaOrg(products, keyword) {
    if (!Array.isArray(products) || products.length === 0) return null;
    return {
        '@type': 'ItemList',
        'name': keyword ? `${keyword} 검색 결과` : '검색 결과',
        'itemListOrder': 'http://schema.org/ItemListOrderDescending',
        'numberOfItems': products.length,
        'itemListElement': products.map((item) => ({
            '@type': 'Product',
            'name': item.title,
            'image': Array.isArray(item.resizedSmallImages) && item.resizedSmallImages.length > 0
                ? item.resizedSmallImages[0]
                : undefined,
            'url': `https://fruitsfamily.com/product/${item.id}${item.title ? '/' + encodeURIComponent(item.title) : ''}`
        }))
    };
}
